// ahri_v5 — domain/routing.js

import { decodeHashRoute, encodeHashRoute } from "../../../scripts/widgetHashRouter.js";
import { AHRI_PAGES } from "../constants/widget.js";

export function encodeFilterMap(params, prefix, map) {
  Object.keys(map || {})
    .sort()
    .forEach((key) => {
      const values = Array.isArray(map[key]) ? map[key] : [];
      const clean = values.map((v) => String(v ?? "").trim()).filter(Boolean);
      [...new Set(clean)].sort().forEach((v) => params.append(`${prefix}${key}`, v));
    });
}

export function decodeFilterMap(params, prefix) {
  const out = {};
  for (const [key, value] of params.entries()) {
    if (!key.startsWith(prefix)) continue;
    const realKey = key.slice(prefix.length);
    if (!realKey) continue;
    (out[realKey] ||= []).push(value);
  }
  return out;
}

// Adds every result-affecting filter dimension to `params`. Empty/default values
// are omitted to keep the URL as short as possible.
export function encodeAhriFilters(params, { filters, advanced, productCodes, warehouse, zip }) {
  encodeFilterMap(params, "f.", filters);
  encodeFilterMap(params, "a.", advanced);
  const codes = [...new Set((Array.isArray(productCodes) ? productCodes : [])
    .map((c) => String(c ?? "").trim()).filter(Boolean))].sort();
  if (codes.length) params.set("pc", codes.join(","));
  const wh = String(warehouse ?? "").trim();
  if (wh) params.set("wh", wh);
  const z = String(zip ?? "").trim();
  if (z) params.set("z", z);
}

export function decodeAhriFilters(params) {
  const codes = String(params.get("pc") || "")
    .split(",")
    .map((c) => c.trim())
    .filter(Boolean);
  return {
    filters: decodeFilterMap(params, "f."),
    advanced: decodeFilterMap(params, "a."),
    productCodes: codes,
    warehouse: params.get("wh") || "",
    zip: params.get("z") || "",
  };
}

export function parseAhriRoute() {
  const decoded = decodeHashRoute(
    typeof window === "undefined" ? "" : window.location.hash,
    { segmentCount: 0, pages: AHRI_PAGES },
  );
  if (!decoded) return null;
  const { params } = decoded;
  const pageNumRaw = parseInt(params.get("page_num") || "", 10);
  // Raw Solr fq escape hatch for deep links that need to scope results by an
  // arbitrary boolean condition the structured f./a. facet scheme can't
  // express -- e.g. the admin eligibility rule-set builder's "preview
  // selected" action linking here with a rule set's AND/OR/NOT predicate
  // already compiled server-side into a fq string. Each entry is ANDed with
  // every other search filter (see buildSearchFilters' use of _raw_fq) --
  // indoor/outdoor/furnace are deliberately separate entries, not one
  // combined OR, because every slot a combination actually carries must
  // comply with its own rule (see api/ahri.py's eligibility_fqs loop).
  const rawFq = [params.get("rawfq_indoor"), params.get("rawfq_outdoor"), params.get("rawfq_furnace")]
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  // Display-only labels for the rule set(s) a rawFq preview link was built
  // from (see AhriEligibilityRuleSets.js's "Preview selected" action) -- never
  // sent to the backend, purely so the widget can show an admin "you're
  // previewing rule set X" banner instead of silently applying a scope with
  // no visible indication of why results are restricted.
  const ruleSetPreviewNames = params.getAll("rsname")
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  return {
    q: params.get("q") || "",
    pageNum: Number.isFinite(pageNumRaw) && pageNumRaw > 1 ? pageNumRaw : 1,
    cart: params.get("cart") === "1",
    system: params.get("system") || "",
    rawFq,
    ruleSetPreviewNames,
    product: params.get("product") || "",
    ...decodeAhriFilters(params),
  };
}

export function buildAhriHash({ q, pageNum, cart, system, product, filters, advanced, productCodes, warehouse, zip, rawFq, ruleSetPreviewNames }) {
  const params = new URLSearchParams();
  const cleanQuery = String(q || "").trim();
  if (cleanQuery && cleanQuery !== "*:*") params.set("q", cleanQuery);
  if (Number(pageNum) > 1) params.set("page_num", String(pageNum));
  if (cart) params.set("cart", "1");
  if (system) params.set("system", String(system));
  if (product) params.set("product", String(product));
  encodeAhriFilters(params, { filters, advanced, productCodes, warehouse, zip });
  // Re-serialize the admin eligibility rule-set preview's deep-link fq/labels
  // (see parseAhriRoute) on every hash rewrite -- otherwise the first
  // search/filter/page change after landing on a "preview selected" link
  // strips them from the URL, so a later refresh loses the preview scope
  // even though it was still applying in memory via rawFqOverrideRef.
  const [indoorFq = "", outdoorFq = "", furnaceFq = ""] = Array.isArray(rawFq) ? rawFq : [];
  if (indoorFq) params.set("rawfq_indoor", indoorFq);
  if (outdoorFq) params.set("rawfq_outdoor", outdoorFq);
  if (furnaceFq) params.set("rawfq_furnace", furnaceFq);
  (Array.isArray(ruleSetPreviewNames) ? ruleSetPreviewNames : [])
    .map((v) => String(v || "").trim())
    .filter(Boolean)
    .forEach((name) => params.append("rsname", name));
  return encodeHashRoute({ segments: [], page: "shop", params });
}

// Stable signature of every input that changes the result set (query, page,
// filters, warehouse/zip). Used to decide whether an inbound route actually
// needs a refetch. Excludes cart/system/product (overlays don't refetch).
export function ahriResultSignature({ q, pageNum, filters, advanced, productCodes, warehouse, zip }) {
  const params = new URLSearchParams();
  const cleanQuery = String(q || "").trim();
  if (cleanQuery && cleanQuery !== "*:*") params.set("q", cleanQuery);
  if (Number(pageNum) > 1) params.set("page_num", String(pageNum));
  encodeAhriFilters(params, { filters, advanced, productCodes, warehouse, zip });
  return params.toString();
}

// Navigation identity: changes here push a new history entry (Back-navigable).
// Overlays (system/product/cart) are the primary navigation boundaries.
// We also treat the empty↔searched transition as a navigation so that the
// first search from the default state creates a Back entry — otherwise pressing
// Back from a search goes straight to the host page instead of the widget home.
export function ahriNavigationKey({ cart, system, product, q }) {
  const queried = String(q || "").trim() ? "q" : "";
  return ["shop", queried, cart ? "1" : "", system || "", product || ""].join("|");
}
